import React, { useState, useEffect, useMemo, useContext } from 'react'

import { Button, Dropdown, Menu, notification, Progress } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { downloadFile, generalErrorHandler, requestWithPromise } from '../../../utils'
import { InternshipStates } from '../../../utils/constants'
import FilesManager, { INTERNSHIP_VALIDATION_TYPE } from '../FilesManager'
import { faCalendar, faChevronDown, faFileAlt, faPaperclip } from '@fortawesome/pro-solid-svg-icons'
import InternshipValidation from './InternshipValidation'
import ValidationUploadConfirmationModal from './ValidationUploadConfirmationModal'
import { getInternshipDocuments } from '../../../utils/api/internship'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { WindowWidthAware } from '../../../HOC/WindowWidthAware'
import { HttpMethods, onError } from '../../../utils/apiHelper'
import { printValidationDocument } from '../../../utils/api/export'
import { useSelector } from 'react-redux'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'

const { ACCEPTED, AUTO_ACCEPTED, SCHEDULE_ADDED, SCHEDULE_VALIDATED, SCHEDULE_TO_VALIDATE, ACCEPTED_UNMANAGED } = InternshipStates
const AUTHORIZED_VALIDATION_STATES = [ACCEPTED, AUTO_ACCEPTED, SCHEDULE_ADDED, SCHEDULE_TO_VALIDATE, ACCEPTED_UNMANAGED]
const VALIDATION_FILES_LIMIT = 1

const DesiderataHeaderButtonBar = ({ desiderataInfo, internship, loading, shifts, useDesiderataLimitation, onBack, onInternshipUpdate }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [printingValidationDocument, setPrintingValidationDocument] = useState(false)
  const [internshipFilesManagerOpen, setInternshipFilesManagerOpen] = useState(false)
  const [canDownloadValidationDoc, setCanDownloadValidationDoc] = useState(false)
  const [canUploadValidationDoc, setCanUploadValidationDoc] = useState(false)
  const [validationDocument, setValidationDocument] = useState(null)
  const [additionalActions, setAdditionalActions] = useState([])
  const [internshipSummaryVisible, setInternshipSummaryVisible] = useState(false)
  const [validationUploadConfirmationVisible, setValidationUploadConfirmationVisible] = useState(false)

  const { eventCodes } = useContext(GlobalContext)

  const disableDesiderataLimitation = useMemo(() => internship.school?.premium, [internship.school])
  const availabilityItems = useMemo(() => (
    <Menu>
      <Menu.ItemGroup title={t('desiderata_header.menu_tailor_made_title')}>
        <Menu.Item disabled style={{ color: 'black' }}>
          <div className='flex-column'>
            {t(disableDesiderataLimitation
              ? 'desiderata_header.menu_desideratas_no_limitation'
              : t('desiderata_header.menu_desideratas_title') + desiderataInfo.daysLeft + t('desiderata_header.menu_desideratas_days')
            )}
            <Progress percent={disableDesiderataLimitation ? 100 : desiderataInfo.percentage} showInfo={false} />
          </div>
        </Menu.Item>
      </Menu.ItemGroup>
    </Menu>
  ), [desiderataInfo.percentage, t, disableDesiderataLimitation])

  const actionItems = <Menu>{additionalActions}</Menu>

  useEffect(() => {
    if (internship) {
      setCanDownloadValidationDoc(internship.state === SCHEDULE_VALIDATED || shifts.filter(s => s.isDesiderata).length > 0)
      setCanUploadValidationDoc(AUTHORIZED_VALIDATION_STATES.includes(internship.state))
    }
  }, [internship, shifts])

  const shouldConfirmValidationUpload =
    internship?.institution?.managed === false &&
    !loading &&
    (!Array.isArray(shifts) || shifts.length === 0)

  useEffect(() => {
    if (internship.studentValidationDocumentId) {
      getInternshipDocuments(user, internship).then(json => {
        if (json?.data) {
          setValidationDocument(json.data.find(d => d.id === internship.studentValidationDocumentId))
        }
      })
    } else {
      setValidationDocument(null)
    }
  }, [internship.id, internship.studentValidationDocumentId, user])

  useEffect(() => {
    displayActionsMenu(canUploadValidationDoc, validationDocument, canDownloadValidationDoc, internship.state, loading)
  }, [canUploadValidationDoc, validationDocument, canDownloadValidationDoc, internship.state, loading])

  const displayActionsMenu = (canUpload, validationDocument, canDownload, state, loading) => {
    const actions = []

    if (canUpload || validationDocument || canDownload) {
      actions.push(
        <Menu.Item
          key='menu-1'
          disabled={printingValidationDocument}
          onClick={() => handleDocumentPrint(validationDocument, state)}
        >
          <FontAwesomeIcon icon={faFileAlt} />&nbsp;
          {t(validationDocument && state === SCHEDULE_VALIDATED ? 'Download signed validation document' : 'Print Validation document')}
        </Menu.Item>
      )
    }

    if (canUpload) {
      actions.push(
        <Menu.Item
          key='menu-2'
          className='desiderata-header-button-upload'
          disabled={printingValidationDocument || loading}
          onClick={handleDocumentUpload}
        >
          <FontAwesomeIcon icon={faPaperclip} />&nbsp;{t('Upload signed validation document')}
        </Menu.Item>
      )
    }

    actions.push(
      <Menu.Item
        key='menu-3'
        onClick={() => setInternshipSummaryVisible(true)}
      >
        <FontAwesomeIcon icon={faCalendar} />&nbsp;{t('View schedule summary')}
      </Menu.Item>
    )

    setAdditionalActions(actions)
  }

  const handleDocumentPrint = (validationDocument, state) => {
    setPrintingValidationDocument(true)

    if (validationDocument && state === SCHEDULE_VALIDATED) {
      // student already uploaded a document, we have to download it
      try {
        downloadFile(`/internship/document/download/${internship.studentValidationDocumentId}`, validationDocument.originalName, user)
        setPrintingValidationDocument(false)
      } catch (err) {
        setPrintingValidationDocument(false)
        generalErrorHandler(err)
      }
    } else {
      const eventCodesTypesLabels = {}

      eventCodes.forEach(e => { eventCodesTypesLabels[e.type] = t('event.code.label.' + e.type) })

      printValidationDocument(internship, eventCodesTypesLabels, user)
        .catch(() => onError(t('spreadsheet_download.error')))
        .finally(() => setPrintingValidationDocument(false))
    }
  }

  const handleDocumentUpload = () => {
    if (loading) {
      return
    }

    if (shouldConfirmValidationUpload) {
      setValidationUploadConfirmationVisible(true)

      return
    }

    setInternshipFilesManagerOpen(true)
  }

  const handleValidationUploadConfirmation = () => {
    setValidationUploadConfirmationVisible(false)
    setInternshipFilesManagerOpen(true)
  }

  const handleValidationUploadConfirmationCancel = () => {
    setValidationUploadConfirmationVisible(false)
  }

  const handleValidationFileDeletion = file => {
    requestWithPromise(`/api/internships/${internship.id}/documents/validation`, HttpMethods.DELETE, {}, user)
      .then(jsonResponse => {
        if (jsonResponse && jsonResponse.data) {
          const newState = jsonResponse.data[0].state

          onInternshipUpdate({
            ...internship,
            state: newState,
            studentValidationDocumentId: null
          })

          notification.success({ message: t('Validation file deleted successfully'), placement: 'bottomRight' })
        }
      })
  }

  const handleValidationFileUpload = file => {
    onInternshipUpdate({ ...internship, state: SCHEDULE_TO_VALIDATE, studentValidationDocumentId: file.id })
  }

  return (
    <div className='flex-row sticky-student-stages-bar' style={user.switchUser ? { top: '41px' } : null}>
      <Button type='primary' onClick={() => { onBack(internship.id, internship.shifts) }}>
        <FontAwesomeIcon icon='chevron-left' />&nbsp;{t('Back')}
      </Button>
      <div className='flex-fill' />
      {loading ? (
        <div className='loading-icon black' style={{ marginRight: '15px' }} />
      ) : null}
      {useDesiderataLimitation && (
        <Dropdown overlay={availabilityItems} trigger={['click']}>
          <Button style={{ marginRight: '5px' }}>
            {t('Available days')}&nbsp;&nbsp;<FontAwesomeIcon icon={faChevronDown} />
          </Button>
        </Dropdown>
      )}
      {additionalActions.length > 0 && (
        <Dropdown overlay={actionItems}>
          <Button>{t('Additional actions')}&nbsp;&nbsp;<FontAwesomeIcon icon={faChevronDown} /></Button>
        </Dropdown>
      )}
      <FilesManager
        width='100%'
        entity={internshipFilesManagerOpen ? internship : null}
        maxFiles={VALIDATION_FILES_LIMIT}
        entityName='Internship'
        onClose={() => setInternshipFilesManagerOpen(false)}
        fileType={INTERNSHIP_VALIDATION_TYPE}
        uploadParams={[{ key: 'validationDocument', value: true }]}
        onFileDelete={handleValidationFileDeletion}
        onUploadsucceeded={handleValidationFileUpload}
      />
      <ValidationUploadConfirmationModal
        visible={validationUploadConfirmationVisible}
        onConfirm={handleValidationUploadConfirmation}
        onCancel={handleValidationUploadConfirmationCancel}
        t={t}
      />
      {internshipSummaryVisible && (
        <WindowWidthAware limit={550}>
          <InternshipValidation
            internship={internship}
            onClose={() => setInternshipSummaryVisible(false)}
            validationViewBottom={false}
            titleDrawer={t('Summary of the internship')}
          />
        </WindowWidthAware>
      )}
    </div>
  )
}

export default DesiderataHeaderButtonBar
